import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { AbsencesService } from '@wilson/absences';
import { GetUserByIdPipe } from '@wilson/account';
import {
  Absence,
  AbsenceStates,
  AbsenceWithDecisionAndHighlighted,
  DayTypes,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import {
  AbsenceApprovalFlowSettingsPurePipe,
  AbsenceDrawerForm,
  AbsenceDrawerFormComponent,
  AbsenceDrawerHelperService,
  AbsenceRequestApprovalSectionComponent,
  AbsenceStatusComponent,
  DeleteAbsenceButtonComponent,
  WithdrawAbsenceButtonComponent,
} from '@wilson/non-domain-specific/absence';
import { LastUpdateStatusComponent } from '@wilson/non-domain-specific/drawer-common';
import { DragAndDropModeService } from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  TimelineUpdateAbsence,
} from '@wilson/shift-timeline/state';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import { TimelineAbsencesService } from '../../../services/timeline-absences.service';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';

@Component({
  selector: 'wilson-timeline-absence-drawer',
  imports: [
    CommonModule,
    TranslateModule,
    NzDrawerModule,
    AbsenceDrawerFormComponent,
    NzButtonModule,
    LastUpdateStatusComponent,
    NzSkeletonModule,
    NzDividerModule,
    NzTooltipModule,
    WithdrawAbsenceButtonComponent,
    DeleteAbsenceButtonComponent,
    AbsenceRequestApprovalSectionComponent,
    AbsenceApprovalFlowSettingsPurePipe,
    AbsenceStatusComponent,
  ],
  providers: [GetUserByIdPipe],
  templateUrl: './timeline-absence-drawer.component.html',
  styleUrls: ['./timeline-absence-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineAbsenceDrawerComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly modalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly message = inject(NzMessageService);
  private readonly absencesService = inject(AbsencesService);
  protected readonly absenceDrawerHelperService = inject(
    AbsenceDrawerHelperService,
  );
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  readonly timelineAbsenceService = inject(TimelineAbsencesService);

  form = new FormGroup<AbsenceDrawerForm>({
    absentFrom: new FormControl(new Date(), [Validators.required]),
    absentTo: new FormControl(new Date(), [Validators.required]),
    absenceCategoryId: new FormControl('', [Validators.required]),
    attachementUrl: new FormControl(''),
    remark: new FormControl(''),
    requestNote: new FormControl(''),
    responseNote: new FormControl(''),
    status: new FormControl(AbsenceStates.open, [Validators.required]),
    halfDayStart: new FormControl(false),
    halfDayEnd: new FormControl(false),
    durationSegmentCurrentlySelectedIndex: new FormControl(0),
    singleDayType: new FormControl(DayTypes.FullDay),
  });

  readonly isSuccessfullySaved = signal<boolean>(false);
  isAbsenceDrawerVisible$ =
    this.absenceDrawerHelperService.isAbsenceDrawerVisible$.pipe(
      distinctUntilChanged(),
    );

  updatedAt: string | undefined;

  protected readonly isLoadingSignal = signal(false);
  protected readonly acceptRequestInFlightSignal = signal(false);
  protected readonly declineRequestInFlightSignal = signal(false);
  protected readonly withdrawRequestInFlightSignal = signal(false);
  protected readonly deleteRequestInFlightSignal = signal(false);

  @Output() performAcceptAbsenceOperation = new EventEmitter<void>();
  protected selectedAbsence!: AbsenceWithDecisionAndHighlighted;
  private readonly destroyed$ = new Subject<void>();

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;

  protected can = inject(AbilityServiceSignal).can;

  async ngOnInit(): Promise<void> {
    await this.loadSelectedAbsence();
  }

  async acceptAbsence(): Promise<void> {
    this.acceptRequestInFlightSignal.set(true);
    try {
      await this.timelineAbsenceService.updateTimelineAbsence(
        this.selectedAbsence,
        AbsenceStates.accepted,
      );
      await this.loadSelectedAbsence(false);
    } finally {
      this.acceptRequestInFlightSignal.set(false);
    }
  }

  async declineAbsence(): Promise<void> {
    this.declineRequestInFlightSignal.set(true);
    try {
      await this.timelineAbsenceService.removeTimelineAbsence(
        this.selectedAbsence,
        AbsenceStates.declined,
      );
    } finally {
      this.declineRequestInFlightSignal.set(false);
    }
  }

  async withdrawAbsence(): Promise<void> {
    this.withdrawRequestInFlightSignal.set(true);
    try {
      await this.timelineAbsenceService.removeTimelineAbsence(
        this.selectedAbsence,
        AbsenceStates.withdrawn,
      );
    } finally {
      this.withdrawRequestInFlightSignal.set(false);
    }
  }

  async refreshDataAfterAbsenceDecision(): Promise<void> {
    await this.loadSelectedAbsence();
  }

  confirmDelete(): NzModalRef | undefined {
    return this.modalService.confirm({
      nzTitle: this.translate.instant(
        'page.shift_timeline.drawer.absence_form.delete_absence',
      ),
      nzContent: this.translate.instant(
        'page.shift_timeline.drawer.delete_absence.popup',
      ),
      nzOkText: this.translate.instant('general.yes'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCancelText: this.translate.instant('general.no'),
      nzOnOk: async () => {
        this.deleteRequestInFlightSignal.set(true);
        try {
          await this.timelineAbsenceService.deleteAbsence(this.selectedAbsence);
        } finally {
          this.deleteRequestInFlightSignal.set(false);
        }
      },
    });
  }

  private async loadSelectedAbsence(showLoading = true): Promise<void> {
    try {
      this.isLoadingSignal.set(showLoading);
      const selectedAbsenceId = await firstValueFrom(
        this.absenceDrawerHelperService.selectedAbsenceId$,
      );
      this.selectedAbsence = (await firstValueFrom(
        this.absencesService.getById(selectedAbsenceId),
      )) as AbsenceWithDecisionAndHighlighted;

      const updatedAt = this.selectedAbsence.updatedAt;
      if (updatedAt) {
        this.updatedAt = this.selectedAbsence.updatedAt;
      }
      this.isLoadingSignal.set(false);
    } catch (error) {
      captureException(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );

      this.isLoadingSignal.set(false);
      this.absenceDrawerHelperService.toggleAbsenceDrawer(false);
    }
  }

  ngOnDestroy(): void {
    this.absenceDrawerHelperService.setSelectedAbsenceId('');
  }

  closeDrawer(): void {
    if (this.form.dirty) {
      this.confirmNavigateAway();
    } else {
      this.absenceDrawerHelperService.toggleAbsenceDrawer(false);
      this.store.dispatch([new SetDemarcatorRange([])]);
      this.dragAndDropModeService.setHorizontalDemarcatorRange({
        left: 0,
        right: 0,
        shiftDuration: null,
        adjustmentNeeded: true,
      });
    }
  }

  confirmNavigateAway(): NzModalRef {
    return this.modalService.confirm({
      nzTitle: this.translate.instant('general.cancel'),
      nzContent: this.translate.instant(
        'page.shift_timeline.drawer.confirm.popup',
      ),
      nzOkText: this.translate.instant('general.yes'),
      nzCancelText: this.translate.instant('general.no'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => {
        this.form.reset();
        this.absenceDrawerHelperService.toggleAbsenceDrawer(false);
        this.store.dispatch([new SetDemarcatorRange([])]);
      },
    });
  }

  async updateAbsence(): Promise<void> {
    this.isSuccessfullySaved.set(false);

    if (!this.selectedAbsence) {
      this.isSuccessfullySaved.set(false);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } else {
      const selectedAbsenceId = await firstValueFrom(
        this.absenceDrawerHelperService.selectedAbsenceId$,
      );
      const formValue = this.form.value;

      if (
        formValue.absenceCategoryId &&
        formValue.absentFrom &&
        formValue.absentTo &&
        formValue.status
      ) {
        const updatedAbsence: Absence & {
          id: string;
        } = {
          absenceCategoryId: formValue.absenceCategoryId,
          absentFrom: formValue.absentFrom.toISOString(),
          absentTo: formValue.absentTo.toISOString(),
          status: formValue.status,
          userId: this.selectedAbsence.userId,
          halfDayStart: formValue.halfDayStart || false,
          halfDayEnd: formValue.halfDayEnd || false,
          id: selectedAbsenceId,
          remark: formValue.remark || '',
          requestNote: formValue.requestNote || null,
          responseNote: formValue.responseNote || null,
        };

        if (formValue.attachementUrl) {
          updatedAbsence.attachementUrl = formValue.attachementUrl;
        }

        try {
          await firstValueFrom(
            this.absencesService.updateAbsence(
              updatedAbsence,
              selectedAbsenceId,
            ),
          );
          this.store.dispatch(
            new TimelineUpdateAbsence({
              ...updatedAbsence,
              isHighlighted: this.selectedAbsence.isHighlighted,
            }),
          );

          this.timelineCommonOperationsService.updateUsersAssignmentsState([
            updatedAbsence.userId,
          ]);
          this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers(
            [updatedAbsence.userId],
          );
          this.timelineCommonOperationsService.updateVacationBalancesOfUsers([
            updatedAbsence.userId,
          ]);
          await this.loadSelectedAbsence(false);
          this.isSuccessfullySaved.set(true);
          this.form.markAsPristine();
          this.message.success(
            this.translate.instant('general.saved_successfully'),
          );
        } catch {
          this.handleErrorDuringUpdateAbsence();
        }
      } else {
        this.handleErrorDuringUpdateAbsence();
      }
    }
  }

  private handleErrorDuringUpdateAbsence(): void {
    this.isSuccessfullySaved.set(false);
    this.message.error(this.translate.instant('general.something_went_wrong'));
  }
}
